export const LIBRARY_GAP = 24
export const LIBRARY_MARGIN = 16
export const LIBRARY_PREFERRED_WIDTH = 520
export const LIBRARY_MIN_BESIDE_WIDTH = 300
export const LIBRARY_MIN_CARDS_WIDTH = 320
export const CARD_COLUMN_MAX = 1200
export const LIBRARY_BOTTOM_BREAKPOINT = 720
export const LIBRARY_MAX_HEIGHT = 560
export const LIBRARY_HEIGHT_RATIO = 0.65
export const LIBRARY_BOTTOM_HEIGHT_RATIO = 0.55

export function inferCardsColumn({
  vw = 1024,
  maxWidth = CARD_COLUMN_MAX,
} = {}) {
  const width = Math.min(Math.max(0, Number(maxWidth) || CARD_COLUMN_MAX), Math.max(0, Number(vw) || 0))
  const left = Math.max(0, (vw - width) / 2)
  return { left, right: left + width, width }
}

function clampBox(left, width, vw, margin) {
  const maxWidth = Math.max(0, vw - margin * 2)
  const nextWidth = Math.min(Math.max(0, width), maxWidth)
  let nextLeft = left
  if (nextLeft + nextWidth > vw - margin) nextLeft = vw - margin - nextWidth
  if (nextLeft < margin) nextLeft = margin
  return { left: nextLeft, width: nextWidth }
}

export function placeLibraryPanel({
  vw = 1024,
  vh = 768,
  cards = { left: 0, right: 0, width: 0 },
  preferredWidth = LIBRARY_PREFERRED_WIDTH,
  minBesideWidth = LIBRARY_MIN_BESIDE_WIDTH,
  minCardsWidth = LIBRARY_MIN_CARDS_WIDTH,
  gap = LIBRARY_GAP,
  margin = LIBRARY_MARGIN,
  bottomBreakpoint = LIBRARY_BOTTOM_BREAKPOINT,
} = {}) {
  const safeVw = Math.max(0, Number(vw) || 0)
  const safeVh = Math.max(0, Number(vh) || 0)
  const available = Math.max(160, safeVh - margin * 2)
  const maxHeight = Math.min(
    available,
    LIBRARY_MAX_HEIGHT,
    Math.round(safeVh * LIBRARY_HEIGHT_RATIO),
  )
  const cardsWidth = Math.max(0, Number(cards.width) || 0)
  const cardsLeft = Number.isFinite(Number(cards.left)) ? Number(cards.left) : 0
  const cardsRight = Number.isFinite(Number(cards.right))
    ? Number(cards.right)
    : cardsLeft + cardsWidth

  const bottomPlacement = () => {
    const width = Math.min(preferredWidth, Math.max(cardsWidth || 0, safeVw - margin * 2))
    return {
      mode: 'bottom',
      ...clampBox(cardsWidth ? cardsLeft : margin, width, safeVw, margin),
      bottom: margin,
      maxHeight: Math.min(available, LIBRARY_MAX_HEIGHT, Math.round(safeVh * LIBRARY_BOTTOM_HEIGHT_RATIO)),
      workspacePadRight: 0,
    }
  }

  const canSplit = safeVw >= minCardsWidth + gap + minBesideWidth + margin * 2
  if (!canSplit || safeVw < bottomBreakpoint) return bottomPlacement()

  const spaceRight = safeVw - margin - (cardsRight + gap)
  if (spaceRight >= minBesideWidth) {
    const width = Math.min(preferredWidth, spaceRight)
    return {
      mode: 'beside',
      ...clampBox(cardsRight + gap, width, safeVw, margin),
      bottom: margin,
      maxHeight,
      workspacePadRight: 0,
    }
  }

  const workspacePadRight = Math.min(
    preferredWidth + gap,
    Math.max(0, safeVw - minCardsWidth - margin),
  )
  const remaining = Math.max(0, safeVw - workspacePadRight)
  const newCardsWidth = Math.min(cardsWidth || remaining, remaining)
  const newCardsLeft = Math.max(0, (remaining - newCardsWidth) / 2)
  const newCardsRight = newCardsLeft + newCardsWidth
  const left = newCardsRight + gap
  const width = Math.min(preferredWidth, Math.max(0, safeVw - margin - left))

  return {
    mode: 'reflow',
    ...clampBox(left, width, safeVw, margin),
    bottom: margin,
    maxHeight,
    workspacePadRight,
  }
}
